import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AcUnit,
  AddCard,
  CreditCard,
  LockOpen,
  Payments,
  Refresh,
  Savings,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  cardAction,
  fetchCards,
  fetchCardWallet,
  topUpCardWallet,
} from '../hooks/hooks';

const money = (v, dp = 2) =>
  Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });

const STATUS_COLOR = {
  ACTIVE: 'success',
  CREATING: 'warning',
  PROCESSING: 'warning',
  FROZEN: 'info',
  SUSPENDED: 'warning',
  TERMINATED: 'default',
  FAILED: 'error',
};

const readError = (err, fallback) =>
  err?.response?.data?.message
  || (err?.response?.data?.errors && Object.values(err.response.data.errors).flat()[0])
  || fallback;

const Cards = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const borderClr = isDark ? 'rgba(255,255,255,0.09)' : '#E2E8F0';

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState(null);
  const [topUpOpen, setTopUpOpen] = useState(false);

  const { data: wallet, isLoading: walletLoading, refetch: refetchWallet } = useQuery({
    queryKey: ['cardWallet'],
    queryFn: fetchCardWallet,
    refetchOnWindowFocus: false,
  });

  const { data: cardsData, isLoading: cardsLoading, refetch: refetchCards } = useQuery({
    queryKey: ['cards'],
    queryFn: fetchCards,
    refetchOnWindowFocus: false,
  });

  const cards = cardsData?.cards || [];
  const balance = Number(wallet?.card_balance_usd || 0);
  const held = Number(wallet?.held_usd || 0);

  const refreshAll = () => {
    refetchWallet();
    refetchCards();
  };

  const actionMutation = useMutation({
    mutationFn: cardAction,
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['cardWallet'] });
      setFeedback({ severity: 'success', message: data?.message || `Card ${vars.action}d.` });
    },
    onError: (err) => setFeedback({
      severity: 'error',
      message: readError(err, 'That did not work. Please try again.'),
    }),
  });

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <CreditCard sx={{ color: '#0BA480', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>Virtual Cards</Typography>
          </Stack>
          <Typography color="text.secondary" fontSize={14}>
            Mastercard virtual cards in USD, funded from your card balance.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={refreshAll}><Refresh /></IconButton>
        </Tooltip>
      </Stack>

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2, borderRadius: '10px' }}
               onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      {wallet?.rate_error && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
          Card pricing is unavailable right now, so top-ups and new cards are disabled.
        </Alert>
      )}

      {/* Wallet */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <MuiCard elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
                <Savings sx={{ fontSize: 18, color: '#0BA480' }} />
                <Typography variant="body2" color="text.secondary">Card Balance</Typography>
              </Stack>
              <Typography variant="h5" fontWeight={700}>
                {walletLoading ? '—' : `$${money(balance)}`}
              </Typography>
              {held > 0 && (
                <Typography variant="caption" color="text.secondary">
                  ${money(held)} pending confirmation
                </Typography>
              )}
            </CardContent>
          </MuiCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MuiCard elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
                <Payments sx={{ fontSize: 18, color: '#F59E0B' }} />
                <Typography variant="body2" color="text.secondary">Collection Balance</Typography>
              </Stack>
              <Typography variant="h5" fontWeight={700}>
                TZS {money(wallet?.collection_balance_tzs)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {wallet?.usd_tzs_rate ? `1 USD = ${money(wallet.usd_tzs_rate)} TZS` : 'rate unavailable'}
              </Typography>
            </CardContent>
          </MuiCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MuiCard elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
            <CardContent>
              <Stack gap={1}>
                <Button
                  variant="contained" fullWidth startIcon={<Savings />}
                  onClick={() => setTopUpOpen(true)}
                  disabled={Boolean(wallet?.rate_error)}
                  sx={{ borderRadius: '3px', textTransform: 'uppercase', fontSize: 12,
                        fontWeight: 600, boxShadow: 0, backgroundColor: '#0BA480',
                        '&:hover': { boxShadow: 'none', backgroundColor: '#0A8F70' } }}
                >
                  Add Card Balance
                </Button>
                <Button
                  variant="outlined" fullWidth startIcon={<AddCard />}
                  onClick={() => navigate('/cards/new')}
                  disabled={Boolean(wallet?.rate_error) || balance <= 0}
                  sx={{ borderRadius: '3px', textTransform: 'uppercase', fontSize: 12, fontWeight: 600 }}
                >
                  New Card
                </Button>
                {balance <= 0 && (
                  <Typography fontSize={11} color="text.secondary">
                    Add card balance before creating a card.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </MuiCard>
        </Grid>
      </Grid>

      {/* Cards */}
      <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
        Your Cards {cardsLoading ? '' : `(${cards.length})`}
      </Typography>

      {cardsLoading ? (
        <Typography color="text.secondary">Loading cards...</Typography>
      ) : cards.length === 0 ? (
        <MuiCard elevation={0} sx={{ border: `1px dashed ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CreditCard sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" mb={0.5}>You have no cards yet.</Typography>
            <Typography fontSize={12.5} color="text.secondary">
              A card needs a cardholder with approved verification.{' '}
              <Button size="small" onClick={() => navigate('/cardholders')}
                      sx={{ textTransform: 'none', fontSize: 12.5, p: 0, minWidth: 0, color: '#0BA480' }}>
                Manage cardholders
              </Button>
            </Typography>
          </CardContent>
        </MuiCard>
      ) : (
        <Grid container spacing={2}>
          {cards.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={c.card_id}>
              <MuiCard elevation={0} sx={{ border: `1px solid ${borderClr}`, borderRadius: '14px',
                                           background: 'linear-gradient(140deg, #0c1a14 0%, #092a1e 55%, #0d5c40 100%)',
                                           color: '#fff' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography fontSize={11} sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: 1 }}>
                      {c.brand || 'CARD'}
                    </Typography>
                    <Chip label={c.status} size="small" color={STATUS_COLOR[c.status] || 'default'}
                          sx={{ fontSize: 10, height: 20 }} />
                  </Stack>

                  <Typography fontSize={18} fontFamily="monospace" letterSpacing={2} mb={1}>
                    {c.masked_number || (c.last4 ? `•••• •••• •••• ${c.last4}` : 'being issued…')}
                  </Typography>

                  <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                    <Box>
                      <Typography fontSize={10} sx={{ color: 'rgba(255,255,255,0.5)' }}>BALANCE</Typography>
                      <Typography fontSize={20} fontWeight={800}>${money(c.balance)}</Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography fontSize={10} sx={{ color: 'rgba(255,255,255,0.5)' }}>EXPIRES</Typography>
                      <Typography fontSize={13}>{c.expiry_date || '––/––'}</Typography>
                    </Box>
                  </Stack>

                  <Typography fontSize={11} sx={{ color: 'rgba(255,255,255,0.45)', mt: 1.5 }}>
                    {c.name_on_card || '—'} · opened {c.created_at ? format(new Date(c.created_at), 'MMM yyyy') : ''}
                  </Typography>

                  <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.12)' }} />

                  <Stack direction="row" gap={1}>
                    <Button size="small" onClick={() => navigate(`/cards/${c.card_id}`)}
                            sx={{ textTransform: 'none', fontSize: 12, color: '#fff' }}>
                      Details
                    </Button>
                    {c.status === 'ACTIVE' && (
                      <Button size="small" startIcon={<AcUnit sx={{ fontSize: 15 }} />}
                              disabled={actionMutation.isPending}
                              onClick={() => actionMutation.mutate({ cardId: c.card_id, action: 'freeze' })}
                              sx={{ textTransform: 'none', fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                        Freeze
                      </Button>
                    )}
                    {c.status === 'FROZEN' && (
                      <Button size="small" startIcon={<LockOpen sx={{ fontSize: 15 }} />}
                              disabled={actionMutation.isPending}
                              onClick={() => actionMutation.mutate({ cardId: c.card_id, action: 'unfreeze' })}
                              sx={{ textTransform: 'none', fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                        Unfreeze
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </MuiCard>
            </Grid>
          ))}
        </Grid>
      )}

      <TopUpDialog open={topUpOpen} onClose={() => setTopUpOpen(false)} wallet={wallet}
                   onResult={setFeedback} />
    </Container>
  );
};

/** Buy USD card balance with TZS from the collection balance. */
const TopUpDialog = ({ open, onClose, wallet, onResult }) => {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setAmount(''); setPin(''); setError(null); }
  }, [open]);

  const minimum = Number(wallet?.minimum_topup_usd || 50);
  const rate = Number(wallet?.usd_tzs_rate || 0);
  const maxUsd = Number(wallet?.max_topup_usd || 0);
  const usd = Number(amount || 0);
  const costTzs = usd * rate;
  const tooMuch = usd > maxUsd;

  const mutation = useMutation({
    mutationFn: topUpCardWallet,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['cardWallet'] });
      queryClient.invalidateQueries({ queryKey: ['settlementSettings'] });
      queryClient.invalidateQueries({ queryKey: ['userDetails'] });
      onClose();
      onResult({ severity: 'success', message: data?.message || 'Card balance added.' });
    },
    onError: (err) => setError(readError(err, 'Top-up failed. Please try again.')),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Add Card Balance</DialogTitle>
      <DialogContent>
        <Typography fontSize={13} color="text.secondary" mb={2}>
          Converts TZS from your collection balance into USD for cards.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, fontSize: 12.5 }}>{error}</Alert>}

        <TextField
          size="small" margin="normal" fullWidth type="number" label="Amount (USD)"
          value={amount} onChange={(e) => setAmount(e.target.value)}
          error={tooMuch}
          helperText={
            tooMuch
              ? `Your collection balance covers about $${money(maxUsd)}`
              : `Minimum $${money(minimum, 0)} · you can add up to $${money(maxUsd)}`
          }
        />

        <Stack gap={0.5} sx={{ my: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography fontSize={13} color="text.secondary">Rate</Typography>
            <Typography fontSize={13}>1 USD = {money(rate)} TZS</Typography>
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography fontSize={13} fontWeight={600}>Taken from collection</Typography>
            <Typography fontSize={14} fontWeight={700} color="#D32F2F">
              TZS {money(costTzs)}
            </Typography>
          </Stack>
        </Stack>

        <TextField
          size="small" margin="normal" fullWidth type="password" label="PIN"
          value={pin} onChange={(e) => setPin(e.target.value)}
          inputProps={{ maxLength: 4, inputMode: 'numeric' }}
          placeholder="Enter your 4-digit PIN"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontSize: 12, textTransform: 'uppercase' }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={mutation.isPending || usd < minimum || tooMuch || !/^\d{4}$/.test(pin)}
          onClick={() => { setError(null); mutation.mutate({ amount_usd: usd, pin }); }}
          sx={{ borderRadius: '3px', textTransform: 'uppercase', fontSize: 12, fontWeight: 600,
                boxShadow: 0, backgroundColor: '#0BA480', '&:hover': { boxShadow: 'none' } }}
        >
          {mutation.isPending ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Cards;
